'use client';

import {useState} from 'react';

import {searchParamsFromProductConfiguration} from '@/modules/commerce/product/configuration';
import type {ProductColorFragment} from '@/modules/commerce/product-color/query';

import type {ProductFilters} from '../../filters/query';
import {ProductColorSelector} from '../../product-color/ProductColorSelector';
import ProductCardBase from '../ProductCardBase';
import {PRODUCT_PAGE_ALL_SLUG} from '../schema/constants';

import {
	type ClothingConfiguration,
	getAllBaseClothingProductColors,
	getClothingConfigurationAppropriateForFilters,
	getConfiguredClothingProductBrands,
} from './configuration';
import type {ClothingProductCoreFragment, CustomClothingProductCoreFragment} from './query';
import {findVariantForSelectedProperties} from './util';

export type ClothingProductCardProps = {
	product: ClothingProductCoreFragment | CustomClothingProductCoreFragment;
	headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
	filters?: ProductFilters;
};

type ColorConfiguration = {
	color: ProductColorFragment;
	configuration: ClothingConfiguration;
};

export default function ClothingProductCard({
	product,
	headingLevel = 2,
	filters,
}: ClothingProductCardProps) {
	const configuration = getClothingConfigurationAppropriateForFilters(product, filters);

	// Try to find a color configuration for each base product color. If we find one, make it
	// selectable in the UI:
	const baseColors = getAllBaseClothingProductColors(product);
	const colorConfigurationsByColorId = baseColors.reduce((acc, color) => {
		const variant = findVariantForSelectedProperties(
			product.variants ?? [],
			null,
			color._id,
			null,
			null,
		);

		if (variant && color.hex && color.name) {
			acc.set(color._id, {
				color: color,
				configuration: {
					...configuration,
					variantId: variant._key,
				},
			});
		}

		return acc;
	}, new Map<string, ColorConfiguration>());
	const colorConfigurations = Array.from(colorConfigurationsByColorId.values());

	// The selected color defaults to null, which means "use the default configuration":
	const [userSelectedColorId, setUserSelectedColorId] = useState<string | null>(null);

	const selectedConfiguration =
		colorConfigurations.find((colorConfig) => colorConfig.color._id === userSelectedColorId)
			?.configuration ?? configuration;

	const brands = getConfiguredClothingProductBrands(product, selectedConfiguration);

	const params = searchParamsFromProductConfiguration(selectedConfiguration);
	const href = `/${PRODUCT_PAGE_ALL_SLUG}/${product.fullSlug}?${params.toString()}`;

	return (
		<>
			<ProductCardBase
				href={href}
				product={product}
				configuration={selectedConfiguration}
				headingLevel={headingLevel}
				brands={brands}
				isConfigurable={product._type === 'commerce.customClothingProduct'}
			>
				{colorConfigurations.length > 1 ? (
					<form
						className="mb-2 flex flex-row gap-2"
						onChange={(e) => {
							const formData = new FormData(e.currentTarget);
							const selectedColorId = formData.get('color');

							if (typeof selectedColorId === 'string') {
								setUserSelectedColorId(selectedColorId);
							}
						}}
					>
						<ProductColorSelector
							size="small"
							colors={colorConfigurations.map((config) => config.color)}
							namePrefix="color"
							defaultConfiguration={userSelectedColorId}
							disableNoneSelection
						/>
					</form>
				) : null}
			</ProductCardBase>
		</>
	);
}
